import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const GRADES = { NONE: "Kein Pflegegrad", GRADE_1: "Pflegegrad 1", GRADE_2: "Pflegegrad 2", GRADE_3: "Pflegegrad 3", GRADE_4: "Pflegegrad 4", GRADE_5: "Pflegegrad 5", UNKNOWN: "Unbekannt" };
const STEPS = ["Persönlich", "Adresse", "Pflege", "Versicherung / Hausarzt", "Kontakt", "Prüfen"];

const F = ({ label, ...p }) => (<label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">{label}</span><Input {...p} /></label>);

export default function CarePatientNew() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ first_name: "", last_name: "", date_of_birth: "", gender: "", phone: "", email: "", care_grade: "UNKNOWN", street: "", house_number: "", postal_code: "", city: "", insurance_provider_name: "", insurance_number: "", doctor_name: "", doctor_practice: "", doctor_phone: "", contact_name: "", contact_relationship: "", contact_phone: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dupe, setDupe] = useState(null);
  const nav = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const next = async () => {
    if (step === 0) {
      if (!f.first_name || !f.last_name) { toast.error("Vor- und Nachname erforderlich"); return; }
      try { const r = await api.post("/care/patients/duplicate-check", f); if (r.data.possible_duplicate) setDupe(r.data.matches); else setDupe(null); } catch {}
    }
    setStep(Math.min(STEPS.length - 1, step + 1));
  };

  const submit = async () => {
    setBusy(true);
    const body = {
      first_name: f.first_name, last_name: f.last_name, date_of_birth: f.date_of_birth || null, gender: f.gender || null,
      phone: f.phone || null, email: f.email || null, care_grade: f.care_grade,
      insurance_provider_name: f.insurance_provider_name || null, insurance_number: f.insurance_number || null,
      doctor_name: f.doctor_name || null, doctor_practice: f.doctor_practice || null, doctor_phone: f.doctor_phone || null,
    };
    if (f.street && f.city) body.location = { type: "HOME", street: f.street, house_number: f.house_number, postal_code: f.postal_code, city: f.city, country: "Deutschland", is_primary: true };
    if (f.contact_name) body.contacts = [{ type: "FAMILY", name: f.contact_name, relationship: f.contact_relationship, phone: f.contact_phone, is_primary: true }];
    try { const r = await api.post("/care/patients", body); setDone(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); setBusy(false); }
  };

  if (done) return (
    <div className="mx-auto max-w-lg py-10 text-center" data-testid="care-create-success">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></div>
      <h1 className="mt-5 font-heading text-2xl font-extrabold text-navy">Patient angelegt</h1>
      <div className="mt-3 rounded-xl bg-slate-50 p-4"><div className="text-xs uppercase tracking-wider text-slate-400">Patient ID</div><div className="mt-1 font-mono text-lg font-bold text-navy" data-testid="care-new-id">{done.patient_number}</div></div>
      <div className="mt-6 flex justify-center gap-3">
        <Button variant="outline" className="rounded-full" onClick={() => nav("/app/employer/pflege/patients")} data-testid="care-goto-list">Zur Liste</Button>
        <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={() => nav(`/app/employer/pflege/patients/${done.id}`)} data-testid="care-goto-detail">Patient öffnen</Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl" data-testid="care-wizard">
      <button onClick={() => nav("/app/employer/pflege/patients")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Zurück</button>
      <h1 className="font-heading text-2xl font-extrabold text-navy">Neuer Patient</h1>
      <div className="my-5 flex flex-wrap gap-2">
        {STEPS.map((s, i) => <span key={s} className={`rounded-full px-3 py-1 text-xs font-semibold ${i === step ? "bg-cyan text-white" : i < step ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{i < step && <Check className="mr-1 inline h-3 w-3" />}{s}</span>)}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        {step === 0 && <div className="grid gap-4 sm:grid-cols-2">
          <F label="Vorname *" value={f.first_name} onChange={set("first_name")} data-testid="w-first" />
          <F label="Nachname *" value={f.last_name} onChange={set("last_name")} data-testid="w-last" />
          <F label="Geburtsdatum" type="date" value={f.date_of_birth} onChange={set("date_of_birth")} data-testid="w-dob" />
          <F label="Telefon" value={f.phone} onChange={set("phone")} />
          <F label="E-Mail" value={f.email} onChange={set("email")} />
          {dupe && <div className="sm:col-span-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700" data-testid="w-dupe">Möglicherweise bereits vorhanden: {dupe.map((m) => m.name).join(", ")}. Sie können trotzdem fortfahren.</div>}
        </div>}
        {step === 1 && <div className="grid gap-4 sm:grid-cols-2">
          <F label="Straße" value={f.street} onChange={set("street")} data-testid="w-street" />
          <F label="Hausnummer" value={f.house_number} onChange={set("house_number")} />
          <F label="PLZ" value={f.postal_code} onChange={set("postal_code")} />
          <F label="Ort" value={f.city} onChange={set("city")} data-testid="w-city" />
        </div>}
        {step === 2 && <label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">Pflegegrad</span>
          <select value={f.care_grade} onChange={set("care_grade")} data-testid="w-grade" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">{Object.entries(GRADES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>}
        {step === 3 && <div className="grid gap-4 sm:grid-cols-2">
          <F label="Krankenkasse" value={f.insurance_provider_name} onChange={set("insurance_provider_name")} data-testid="w-ins" />
          <F label="Versichertennummer" value={f.insurance_number} onChange={set("insurance_number")} />
          <F label="Hausarzt" value={f.doctor_name} onChange={set("doctor_name")} data-testid="w-doc" />
          <F label="Praxis" value={f.doctor_practice} onChange={set("doctor_practice")} />
          <F label="Arzt Telefon" value={f.doctor_phone} onChange={set("doctor_phone")} />
        </div>}
        {step === 4 && <div className="grid gap-4 sm:grid-cols-2">
          <F label="Kontakt Name" value={f.contact_name} onChange={set("contact_name")} data-testid="w-contact" />
          <F label="Beziehung" value={f.contact_relationship} onChange={set("contact_relationship")} />
          <F label="Telefon" value={f.contact_phone} onChange={set("contact_phone")} />
        </div>}
        {step === 5 && <div className="space-y-2 text-sm" data-testid="w-review">
          <div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">Name</span><span className="font-semibold text-navy">{f.first_name} {f.last_name}</span></div>
          <div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">Pflegegrad</span><span className="font-semibold text-navy">{GRADES[f.care_grade]}</span></div>
          <div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">Adresse</span><span className="font-semibold text-navy">{[f.street, f.city].filter(Boolean).join(", ") || "—"}</span></div>
          <div className="flex justify-between py-1.5"><span className="text-slate-500">Krankenkasse</span><span className="font-semibold text-navy">{f.insurance_provider_name || "—"}</span></div>
        </div>}
      </div>

      <div className="mt-5 flex justify-between">
        <Button variant="outline" className="rounded-full" disabled={step === 0} onClick={() => setStep(step - 1)}><ArrowLeft className="mr-1 h-4 w-4" /> Zurück</Button>
        {step < STEPS.length - 1
          ? <Button className="rounded-full bg-navy text-white hover:bg-navy/90" onClick={next} data-testid="w-next">Weiter <ArrowRight className="ml-1 h-4 w-4" /></Button>
          : <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" disabled={busy} onClick={submit} data-testid="w-submit">{busy ? "Speichern…" : "Patient anlegen"}</Button>}
      </div>
    </div>
  );
}
